import type { StateCreator } from 'zustand';
import type File from './File';
import type { FileUpdate } from './File';
import type Group from './Group';
import type User from './User';
import type { WebSocketMessage } from './WebSocketMessage';

/**
 * EventsMap defines all supported events for the in-memory pub/sub micro-event system.
 * Extend this type to add new events and their payloads as needed.
 */
type EventsMap = {
  /** User logout event (no payload). */
  makeLogout: void;
  /** Triggers a refresh of "batches" (no payload). */
  refreshBatches: void;
  /** Absolute batch progress from AI classification realtime events (last write wins). */
  batchProgressUpdated: {
    batchId: number;
    processed: number;
    total: number;
    pending?: number;
    processing?: number;
    succeeded?: number;
    failed?: number;
    canceled?: number;
    eventTs?: number;
    batchStatus?: string;
    fileId?: number;
    fileName?: string;
    fileStatus?: string;
  };
  /** Notifies user creation (no payload, used for refetch/list updates). */
  createUser: void;
  /** Notifies group creation (no payload). */
  createGroup: void;
  /**
   * Mark-as-read succeeded (e.g. from useOpenFile); mounted file lists should set isUnRead false for these ids.
   */
  filesMarkedAsReadLocally: { fileIds: number[] };
  /** Triggers refresh for group details, with payload: updated group object. */
  refreshGroupDetail: { updatedGroup: Group };
  /** Notifies that files have been updated, with payload: array of updated files. */
  onFilesUpdated: { files: FileUpdate[] };
  /** Notifies that files have been created elsewhere (e.g. folder picker); lists may prepend matching rows. */
  onFilesCreated: { files: File[] };
  /** Notifies that files have been deleted, with payload: array of deleted files. */
  onFilesDeleted: { files: File[] };
  /** Notifies that a public share link was revoked for a file (payload: fileId). Use to clear isPublicShare in file list. */
  onFilePublicLinkRevoked: { fileId: number };
  /** Notifies that users have been updated, with payload: array of updated users. */
  onUsersUpdated: { users: User[] };
  /** Notifies that users have been deleted, with payload: array of deleted users. */
  onUsersDeleted: { users: User[] };
  /** Notifies that groups have been updated, with payload: array of updated groups. */
  onGroupsUpdated: { groups: Group[] };
  /** Notifies that groups have been deleted, with payload: array of deleted groups. */
  onGroupsDeleted: { groups: Group[] };
  /** EventHub ReceiveEvent: server WebSocketMessage (id, type, timestamp, data, userId, groupId, companyId). */
  webSocketEvent: WebSocketMessage;
  /** Fired when the global dropzone overlay should appear (no payload). */
  globalDropzoneDragOver: void;
  /** Fired when the global dropzone overlay should hide (no payload). */
  globalDropzoneDragLeave: void;
};

/**
 * Utility type: Listener function for a given event type.
 * - If the event payload is void, the listener takes no params.
 * - Otherwise, it receives the payload object.
 */
type Listener<T> = T extends void ? () => void : (payload: T) => void;

/**
 * Global registry for all event listeners (in-memory Map per event).
 * This is NOT part of Zustand — listeners survive React unmounts/rerenders.
 *
 * Use this as a simple micro-event bus. Keys correspond to EventsMap keys.
 */
const listeners: { [K in keyof EventsMap]: Set<Listener<EventsMap[K]>> } = {
  makeLogout: new Set(),
  refreshBatches: new Set(),
  batchProgressUpdated: new Set(),
  createUser: new Set(),
  createGroup: new Set(),
  filesMarkedAsReadLocally: new Set(),
  refreshGroupDetail: new Set(),
  onFilesUpdated: new Set(),
  onFilesCreated: new Set(),
  onFilesDeleted: new Set(),
  onFilePublicLinkRevoked: new Set(),
  onUsersUpdated: new Set(),
  onUsersDeleted: new Set(),
  onGroupsUpdated: new Set(),
  onGroupsDeleted: new Set(),
  webSocketEvent: new Set(),
  globalDropzoneDragOver: new Set(),
  globalDropzoneDragLeave: new Set(),
};

/**
 * Vite hot module reload (HMR) support:
 * Clears all listeners when the module is replaced, to avoid memory leaks or duplicate listeners.
 */
if (import.meta && (import.meta as any).hot) {
  (import.meta as any).hot.dispose(() => {
    // Clear all registered listeners for each event key
    (Object.keys(listeners) as (keyof EventsMap)[]).forEach((k) => listeners[k].clear());
  });
}

/**
 * Register an event listener for a given event.
 * Returns an unsubscribe function to detach the listener.
 *
 * @param event - Event name to subscribe to
 * @param cb - Listener function (signature depends on event payload)
 * @returns Unsubscribe function
 */
function addListener<K extends keyof EventsMap>(event: K, cb: Listener<EventsMap[K]>): () => void {
  listeners[event].add(cb);
  // Return unsubscribe function
  return () => listeners[event].delete(cb);
}

/**
 * Emit an event, optionally supplying a payload. All listeners fire synchronously.
 * Errors in one listener do not affect others.
 *
 * Overloads for TypeScript:
 *   - For void events: payload param omitted
 *   - For events with payload: pass argument
 *
 * @param event - Event name to emit
 * @param payload - Optional payload for this event
 */
function emitEvent<K extends keyof EventsMap>(event: K): void;
function emitEvent<K extends keyof EventsMap>(event: K, payload: EventsMap[K]): void;
function emitEvent<K extends keyof EventsMap>(
  event: K,
  ...args: EventsMap[K] extends void ? [] : [EventsMap[K]]
) {
  // Take a snapshot to avoid issues if listeners are removed during firing
  const snapshot = Array.from(listeners[event]);
  for (const cb of snapshot) {
    try {
      // Type coercion is used to accommodate both void and non-void event signatures
      (cb as any)(args[0]);
    } catch {
      // Silently ignore errors in individual listeners so other listeners still fire
    }
  }
}

/**
 * Utility typed API for consuming and emitting events.
 */
type OnAPI = {
  [K in keyof EventsMap]: (handler: Listener<EventsMap[K]>) => () => void;
};
type EmitAPI = {
  [K in keyof EventsMap]: EventsMap[K] extends void ? () => void : (payload: EventsMap[K]) => void;
};

/**
 * Zustand "slice" interface for the cross-app events API.
 * Use the API from anywhere in the app via the Zustand store.
 */
export interface EventsSlice {
  events: {
    /** Subscribe to events. */
    on: OnAPI;
    /** Emit/publish events. */
    emit: EmitAPI;
  };
}

/**
 * Returns an EventsSlice for the Zustand store. Inject into your store setup.
 * This enables in-app pub/sub patterns and allows your business logic to
 * react to application domain events easily.
 */
export const createEventsSlice: StateCreator<EventsSlice, [], [], EventsSlice> = () => ({
  events: {
    // Register listeners for each supported event
    on: {
      makeLogout: (handler) => addListener('makeLogout', handler),
      refreshBatches: (handler) => addListener('refreshBatches', handler),
      batchProgressUpdated: (handler) => addListener('batchProgressUpdated', handler),
      createUser: (handler) => addListener('createUser', handler),
      createGroup: (handler) => addListener('createGroup', handler),
      filesMarkedAsReadLocally: (handler) => addListener('filesMarkedAsReadLocally', handler),
      refreshGroupDetail: (handler) => addListener('refreshGroupDetail', handler),
      onFilesUpdated: (handler) => addListener('onFilesUpdated', handler),
      onFilesCreated: (handler) => addListener('onFilesCreated', handler),
      onFilesDeleted: (handler) => addListener('onFilesDeleted', handler),
      onFilePublicLinkRevoked: (handler) => addListener('onFilePublicLinkRevoked', handler),
      onUsersUpdated: (handler) => addListener('onUsersUpdated', handler),
      onUsersDeleted: (handler) => addListener('onUsersDeleted', handler),
      onGroupsUpdated: (handler) => addListener('onGroupsUpdated', handler),
      onGroupsDeleted: (handler) => addListener('onGroupsDeleted', handler),
      webSocketEvent: (handler) => addListener('webSocketEvent', handler),
      globalDropzoneDragOver: (handler) => addListener('globalDropzoneDragOver', handler),
      globalDropzoneDragLeave: (handler) => addListener('globalDropzoneDragLeave', handler),
    },
    // Emit/trigger each supported event, with or without payload
    emit: {
      makeLogout: () => emitEvent('makeLogout'),
      refreshBatches: () => emitEvent('refreshBatches'),
      batchProgressUpdated: (payload) => emitEvent('batchProgressUpdated', payload),
      createUser: () => emitEvent('createUser'),
      createGroup: () => emitEvent('createGroup'),
      filesMarkedAsReadLocally: (payload) => emitEvent('filesMarkedAsReadLocally', payload),
      refreshGroupDetail: (payload) => emitEvent('refreshGroupDetail', payload),
      onFilesUpdated: (payload) => emitEvent('onFilesUpdated', payload),
      onFilesCreated: (payload) => emitEvent('onFilesCreated', payload),
      onFilesDeleted: (payload) => emitEvent('onFilesDeleted', payload),
      onFilePublicLinkRevoked: (payload) => emitEvent('onFilePublicLinkRevoked', payload),
      onUsersUpdated: (payload) => emitEvent('onUsersUpdated', payload),
      onUsersDeleted: (payload) => emitEvent('onUsersDeleted', payload),
      onGroupsUpdated: (payload) => emitEvent('onGroupsUpdated', payload),
      onGroupsDeleted: (payload) => emitEvent('onGroupsDeleted', payload),
      webSocketEvent: (payload) => emitEvent('webSocketEvent', payload),
      globalDropzoneDragOver: () => emitEvent('globalDropzoneDragOver'),
      globalDropzoneDragLeave: () => emitEvent('globalDropzoneDragLeave'),
    },
  },
});
